// ─────────────────────────────────────────────────────────────────────────────
// Finance Core design tokens.
//
// This app themes via GLOBAL SEMANTIC TOKENS defined in theme/styles.js
// (bg.surface, text.heading, …). Those tokens are `{ default: dark, _light }`
// pairs, so they DO flip with color mode — the flip happens in CSS, which is
// exactly why finance-core consumes them as CSS variables rather than through
// useColorModeValue: the var re-resolves on toggle with no re-render, and the
// same string works BOTH as a Chakra prop (bg=..., color=...) AND inside inline
// style={{}} objects (native <option>, <b>, box-shadow strings, etc.).
//
// An earlier version of this header claimed the tokens were single-value dark
// navy. That was wrong, and acting on it produced dark-only styling downstream.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const cv = (token) => `var(--chakra-colors-${token.replace(/\./g, '-')})`;

// Resolved once — values are constant (no hook state needed), but exposed as a
// hook for a consistent call-site API and future-proofing.
const TOKENS = {
	// Surfaces (navy hierarchy: app 900 → surface 800 → elevated 700 → input 500)
	pageBg: cv('bg.app'),
	surface: cv('bg.surface'),
	surfaceElevated: cv('bg.elevated'),
	surfaceSubtle: cv('bg.subtle'), // zebra — one step BELOW surface, distinct from hover
	tableHeader: cv('erp.table.header'), // accent-tinted header row
	inputBg: cv('bg.input'),
	inputHoverBg: cv('bg.elevated'),

	// Borders
	border: cv('border.default'),
	borderSubtle: cv('border.subtle'),
	borderStrong: cv('border.default'),
	focusRing: cv('accent.gold'),

	// Text (white heading / gray body / muted — matches text.* tokens)
	textPrimary: cv('text.heading'),
	textSecondary: cv('text.body'),
	textMuted: cv('text.muted'),
	// Text ON the gold accent (selected tabs, gold fills) — near-black.
	// Was cv('text.black'), a token that resolved to undefined (missing key in
	// the theme), silently rendering white-on-gold (~2:1 contrast).
	textOnAccent: cv('text.onAccent'),

	// Accents
	accent: cv('accent.gold'),
	accentSubtle: cv('bg.elevated'), // surface chip behind a gold icon
	link: cv('info'),

	// Semantic data colors.
	//
	// These MUST be the theme's real `success` / `danger` / `warning` / `info`
	// semantic tokens. A previous version emitted `tone.success` etc. — a
	// namespace that never existed in theme/styles.js — and an undefined CSS
	// variable is dropped silently: every signed amount, status dot, banner and
	// alarm branch in all ~42 finance-core modules rendered as plain body text,
	// in both modes, with no error anywhere. If you rename these, grep the theme
	// first; nothing warns you when a var() has no definition.
	money: cv('success'),
	moneyNeg: cv('danger'),
	success: cv('success'),
	danger: cv('danger'),
	warning: cv('warning'),
	info: cv('info'),

	iconMuted: cv('text.muted'),

	// Elevation — literal so it also works in inline styles.
	shadowCard: 'var(--chakra-shadows-card)', // semantic shadow — light gets a soft key, dark a deep one
};

export function useFinanceUI() {
	return TOKENS;
}

// ─────────────────────────────────────────── employee options ──────────────
/**
 * Employee dropdown options, sourced from the app-wide user list already in
 * Redux (`state.user.users`, populated at boot) — NO extra API call, and no
 * dependency on any specific /users route. Returns [{ _id, name }] sorted by
 * name. Used by every finance-core screen that assigns a document to a person.
 */
export function useEmployeeOptions() {
	const users = useSelector((state) => state.user?.users);
	return useMemo(() => {
		const list = Array.isArray(users) ? users : [];
		return list
			.map((u) => ({
				_id: u._id,
				name: (u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.userName || u.username || u.email || '').trim(),
			}))
			.filter((u) => u._id)
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [users]);
}

// ─────────────────────────────────────────── idempotency helper ────────────
/**
 * Fresh Idempotency-Key (a raw UUID token).
 *
 * Do NOT mint one per modal-open and reuse it for the whole session — the
 * backend stores 4xx outcomes per key + body hash, so an edited resubmit under
 * a stale key returns 409 "already used for a different request". Use
 * `useIdempotentSubmit().keyFor(body)` (finance-core/_ui/hooks) instead, which
 * ties the key to the body: equal bodies share a key (safe double-click/retry
 * de-dupe), a changed body mints a fresh one. This function is the low-level
 * generator that hook builds on.
 */
export const newIdempotencyKey = () => {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) return `fc-${crypto.randomUUID()}`;
	// Fallback for older browsers — uniqueness, not cryptographic strength.
	return `fc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

// ─────────────────────────────────────────── formatting helpers ────────────
export const fmtMoney = (n, currency = '') => {
	const v = Number(n || 0).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	return currency ? `${v} ${currency}` : v;
};

export const fmtDate = (d) =>
	d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const fmtDateTime = (d) =>
	d
		? new Date(d).toLocaleString(undefined, {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
		  })
		: '—';

// The canonical status → tone mapping now lives in components.jsx (STATUS_TONE,
// consumed by StatusBadge). The old Chakra-colorScheme map here was dead and
// pointed at light-mode pastel palettes, so it has been removed.

export const statusLabel = (s) => (s ? String(s).replace(/_/g, ' ') : '');
